import { Trans, useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { plusIcon, trashIcon } from '@progress/kendo-svg-icons';
import { DlpLabelModel, DlpProjectModel, DlpQuotaProfileRowModel } from 'services/DlpConfiguration/DlpConfigurationService';

interface ProfileRowsEditorProps {
    project: DlpProjectModel;
    rows: DlpQuotaProfileRowModel[];
    onChange: (rows: DlpQuotaProfileRowModel[]) => void;
    disabled: boolean;
}

export function ProfileRowsEditor({ project, rows, onChange, disabled }: ProfileRowsEditorProps) {
    const { t } = useTranslation();
    const stratified = project.group_by.includes('label');
    const labelById = new Map(project.dlp_labels.map(label => [label.id, label]));

    const update = (index: number, patch: Partial<DlpQuotaProfileRowModel>) =>
        onChange(rows.map((r, i) => i === index ? { ...r, ...patch } : r));

    const addRow = () => onChange([...rows, {
        profile_name: rows[0]?.profile_name ?? '',
        project_id: project.id,
        channel: project.channels[0] ?? null,
        label: stratified ? (project.dlp_labels[0]?.id ?? null) : null,
        max_docs: null
    }]);

    return (
        <div className="raw-rows">
            {rows.map((row, i) => {
                // A value no longer valid for the project stays selectable, so it's visible until fixed
                const channels = row.channel && !project.channels.includes(row.channel) ? [row.channel, ...project.channels] : project.channels;
                const labels: DlpLabelModel[] = row.label && !project.dlp_labels.some(l => l.id === row.label)
                    ? [{ id: row.label, name: row.label, parent_id: null }, ...project.dlp_labels]
                    : project.dlp_labels;
                return (
                    <div key={i} className={`raw-row ${disabled ? 'disabled' : ''}`}>
                        <select className="lm-input sm" value={row.channel ?? ''} disabled={disabled} onChange={e => update(i, { channel: e.target.value || null })}>
                            <option value="">{t('dlpConfiguration.profiles.anyChannel')}</option>
                            {channels.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {stratified ? (
                            <select className="lm-input sm" value={row.label ?? ''} disabled={disabled} onChange={e => update(i, { label: e.target.value || null })}>
                                <option value="">{t('dlpConfiguration.profiles.anyLabel')}</option>
                                {labels.map(l => {
                                    const parent = l.parent_id ? labelById.get(l.parent_id) : undefined;
                                    return <option key={l.id} value={l.id}>{parent ? `${parent.name} › ${l.name}` : l.name}</option>;
                                })}
                            </select>
                        ) : (
                            <span className="raw-row-na">{t('dlpConfiguration.profiles.notLabelStratified')}</span>
                        )}
                        <input
                            className="lm-input sm"
                            type="number"
                            placeholder={t('dlpConfiguration.profiles.maxDocs')}
                            disabled={disabled}
                            value={row.max_docs ?? ''}
                            onChange={e => update(i, { max_docs: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                        />
                        <button type="button" className="icon-btn" disabled={disabled} title={t('general.remove')} onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
                            <SvgIcon icon={trashIcon} size="small" />
                        </button>
                    </div>
                );
            })}
            {rows.length === 0 && <div className="empty-note">{t('dlpConfiguration.profiles.noRows')}</div>}
            {!disabled && (
                <button type="button" className="lm-btn-secondary sm" onClick={addRow}>
                    <SvgIcon icon={plusIcon} size="small" /> <Trans>dlpConfiguration.profiles.addRow</Trans>
                </button>
            )}
        </div>
    );
}
